import { useCallback, useEffect, useRef, useState } from 'react'
import { MapVersion } from '@ivy-monorepo/shared'
import { apiClient } from '../services/api-client'
import { NearbyVenue } from '../types/liveMap'

/**
 * Fetches nearby venues from GET {apiBaseUrl}user/venues/nearby (the same
 * endpoint the mobile app hits — no backend changes). Exposes `setVenues` so the
 * socket hook can patch the in-memory list in place.
 *
 * Results accumulate: each fetch merges by publicId (refreshing/adding venues in
 * the new viewport) rather than replacing, so panning/zooming doesn't drop the
 * venues you already loaded. A soft cap keeps the set from growing unbounded.
 */

export interface LatLng {
  lat: number
  lng: number
}

/** Upper bound on accumulated venues (drops the oldest-seen beyond this). */
const MAX_ACCUMULATED_VENUES = 1500

/** Merge incoming venues into the existing set, newest data winning per id. */
function mergeVenues(prev: NearbyVenue[], incoming: NearbyVenue[]): NearbyVenue[] {
  const byId = new Map<string, NearbyVenue>()
  for (const v of prev) byId.set(v.publicId, v)
  // Setting an existing key updates its value but keeps its original position,
  // so freshly fetched venues append at the end (oldest stay first).
  for (const v of incoming) byId.set(v.publicId, v)
  const merged = Array.from(byId.values())
  return merged.length > MAX_ACCUMULATED_VENUES
    ? merged.slice(merged.length - MAX_ACCUMULATED_VENUES)
    : merged
}

interface UseVenuesNearbyResult {
  venues: NearbyVenue[]
  setVenues: React.Dispatch<React.SetStateAction<NearbyVenue[]>>
  loading: boolean
  error: string | null
  reload: () => void
}

/** The nearby endpoint may return a bare array or a wrapped envelope. */
function coerceToList(data: unknown): NearbyVenue[] {
  if (Array.isArray(data)) return data as NearbyVenue[]
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>
    for (const key of ['venues', 'items', 'data', 'results']) {
      if (Array.isArray(obj[key])) return obj[key] as NearbyVenue[]
    }
  }
  return []
}

export function useVenuesNearby(
  center: LatLng | null,
  mapMode: MapVersion = MapVersion.New
): UseVenuesNearbyResult {
  const [venues, setVenues] = useState<NearbyVenue[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const requestIdRef = useRef(0)

  const fetchVenues = useCallback(
    async (c: LatLng) => {
      const requestId = ++requestIdRef.current
      setLoading(true)
      setError(null)
      try {
        const response = await apiClient.get('/user/venues/nearby', {
          params: {
            latitude: String(c.lat),
            longitude: String(c.lng),
            mapCenterLatitude: String(c.lat),
            mapCenterLongitude: String(c.lng),
            mapMode,
          },
        })
        if (requestId !== requestIdRef.current) return // stale response
        const list = coerceToList(response.data).filter(v => v.isHidden !== true && v.isClosed !== true)
        setVenues(prev => mergeVenues(prev, list))
      } catch (err) {
        if (requestId !== requestIdRef.current) return
        console.error('Failed to load nearby venues:', err)
        setError('Failed to load nearby venues.')
      } finally {
        if (requestId === requestIdRef.current) setLoading(false)
      }
    },
    [mapMode]
  )

  useEffect(() => {
    if (!center) return
    fetchVenues(center)
  }, [center, fetchVenues])

  const reload = useCallback(() => {
    if (center) fetchVenues(center)
  }, [center, fetchVenues])

  return { venues, setVenues, loading, error, reload }
}
